/**
 * BookingsList — /crm/bookings
 * Global bookings list with search, filter, and status summary
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, Loader2, ChevronRight, Calendar, RefreshCw,
  CheckCircle2, XCircle, Clock, FileText,
} from "lucide-react";
import { bookingApi } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";
import { hasCapability } from "@/services/api";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const C = {
  ivory:   "#FBF7F2",
  sand:    "#FAF6F0",
  gold:    "#C9A15F",
  charcoal:"#3A3330",
  slate:   "#7A706A",
  divider: "rgba(201,161,95,0.15)",
};

const STATUS_CONFIG = {
  DRAFT:              { bg: "#F3F4F6", text: "#374151", label: "Draft" },
  PENDING_ACCEPTANCE: { bg: "#FEF3C7", text: "#92400E", label: "Pending Acceptance" },
  CONFIRMED:          { bg: "#D1FAE5", text: "#065F46", label: "Confirmed" },
  COMPLETED:          { bg: "#DBEAFE", text: "#1E40AF", label: "Completed" },
  CANCELLED:          { bg: "#FEE2E2", text: "#991B1B", label: "Cancelled" },
};

const STATUS_OPTIONS = ["", "DRAFT", "PENDING_ACCEPTANCE", "CONFIRMED", "COMPLETED", "CANCELLED"];

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.DRAFT;
  return (
    <span className="px-2 py-0.5 text-xs font-medium rounded-full" style={{ background: cfg.bg, color: cfg.text }}>
      {cfg.label}
    </span>
  );
}

const fmtCurrency = (v, currency = "INR") =>
  v != null ? `${currency === "INR" ? "₹" : currency} ${Number(v).toLocaleString("en-IN")}` : "—";

const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—";

export default function BookingsList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  const canRead = hasCapability(user, "booking.read");

  const fetchBookings = useCallback(async () => {
    if (!canRead) return;
    setLoading(true);
    try {
      const params = { page, page_size: 25 };
      if (filterStatus) params.status = filterStatus;
      const { data } = await bookingApi.list(params);
      setBookings(data.items || []);
      setTotal(data.total || 0);
    } catch {
      toast.error("Failed to load bookings");
    } finally {
      setLoading(false);
    }
  }, [page, filterStatus, canRead]);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  if (!canRead) {
    return (
      <div className="p-8 text-center" style={{ color: C.slate }}>
        You don't have permission to view bookings.
      </div>
    );
  }

  const filtered = searchTerm
    ? bookings.filter(b =>
        (b.booking_number || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (b.opportunity_name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (b.customer_name || "").toLowerCase().includes(searchTerm.toLowerCase())
      )
    : bookings;

  // Summary counts from loaded data
  const counts = bookings.reduce((acc, b) => {
    acc[b.status] = (acc[b.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div data-testid="bookings-list-page" className="min-h-screen p-6" style={{ background: C.ivory }}>
      <div className="max-w-5xl mx-auto space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold" style={{ color: C.charcoal }}>Customer Bookings</h1>
            <p className="text-sm mt-0.5" style={{ color: C.slate }}>{total} total bookings</p>
          </div>
          <button onClick={fetchBookings} className="p-1.5 rounded hover:bg-black/5">
            <RefreshCw className="w-4 h-4" style={{ color: C.slate }} />
          </button>
        </div>

        {/* Summary tiles */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {["CONFIRMED", "PENDING_ACCEPTANCE", "DRAFT", "CANCELLED"].map(s => {
            const cfg = STATUS_CONFIG[s];
            return (
              <button
                key={s}
                data-testid={`booking-filter-${s}`}
                onClick={() => setFilterStatus(filterStatus === s ? "" : s)}
                className="rounded-lg p-3 text-left transition-all"
                style={{
                  background: filterStatus === s ? cfg.bg : C.sand,
                  border: `1px solid ${filterStatus === s ? cfg.text + "44" : C.divider}`,
                }}
              >
                <div className="text-lg font-bold" style={{ color: cfg.text }}>{counts[s] || 0}</div>
                <div className="text-xs mt-0.5" style={{ color: C.slate }}>{cfg.label}</div>
              </button>
            );
          })}
        </div>

        {/* Search + filter */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: C.slate }} />
            <Input
              data-testid="booking-search-input"
              className="pl-9"
              placeholder="Search by booking number, opportunity, or customer..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
          <select
            data-testid="booking-status-filter"
            className="border rounded-md px-3 py-2 text-sm"
            style={{ borderColor: C.divider, color: C.slate }}
            value={filterStatus}
            onChange={e => { setFilterStatus(e.target.value); setPage(1); }}
          >
            {STATUS_OPTIONS.map(s => (
              <option key={s} value={s}>{s ? STATUS_CONFIG[s].label : "All Statuses"}</option>
            ))}
          </select>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center h-32" style={{ color: C.slate }}>
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading...
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-sm text-center py-12" style={{ color: C.slate }}>
            {searchTerm || filterStatus ? "No bookings match your filters" : "No bookings yet"}
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map(b => (
              <div
                key={b.id}
                data-testid={`booking-row-${b.id}`}
                className="rounded-xl p-4 flex items-center gap-4 cursor-pointer hover:shadow-sm transition-all"
                style={{ background: C.sand, border: `1px solid ${C.divider}` }}
                onClick={() => navigate(`/crm/bookings/${b.id}`)}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm" style={{ color: C.charcoal }}>{b.booking_number}</span>
                    <StatusBadge status={b.status} />
                  </div>
                  <div className="text-xs mt-0.5 truncate" style={{ color: C.slate }}>
                    {b.opportunity_name || b.opportunity_id}
                    {b.customer_name && ` · ${b.customer_name}`}
                  </div>
                  <div className="text-xs mt-0.5" style={{ color: C.slate }}>
                    Created {fmtDate(b.created_at)}
                    {b.confirmed_at && ` · Confirmed ${fmtDate(b.confirmed_at)}`}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-semibold" style={{ color: C.charcoal }}>
                    {fmtCurrency(b.booking_value, b.currency)}
                  </div>
                  <div className="text-xs mt-0.5" style={{ color: C.slate }}>{b.currency}</div>
                </div>
                <ChevronRight className="w-4 h-4 shrink-0" style={{ color: C.slate }} />
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {total > 25 && (
          <div className="flex justify-center gap-2 pt-2">
            <button
              disabled={page === 1}
              onClick={() => setPage(p => p - 1)}
              className="px-3 py-1.5 text-xs rounded border disabled:opacity-40"
              style={{ color: C.slate }}
            >
              Previous
            </button>
            <span className="px-3 py-1.5 text-xs" style={{ color: C.slate }}>Page {page}</span>
            <button
              disabled={page * 25 >= total}
              onClick={() => setPage(p => p + 1)}
              className="px-3 py-1.5 text-xs rounded border disabled:opacity-40"
              style={{ color: C.slate }}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
